import React, { useMemo, useState } from 'react';
import { Box, Card, CardContent, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { TooltipProps } from 'recharts';
import { formatNumber, formatNumberWhole } from 'c-lib';
import { NetworkRequestState } from 'c-data-layer';
import type { ChartReportPart } from 'c-reports/Types';
import { ReportType } from 'c-reports/Types';
import { StaticChartWidgetReport } from 'c-main/Components/Campaign/CampaignAnalytics/summaryWidgetReports';
import type { ReportControlsState } from 'c-reports/Components';

export type SeriesColors = { expected: string; classic: string; digital: string; both: string };
export type SeriesLabels = {
    expected: string;
    expectedClassic: string;
    expectedDigital: string;
    classic: string;
    digital: string;
    total: string;
};

const whole = (v: number) => formatNumberWhole(v);

export const ChartCard: React.FC<{
    title: string;
    action?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, action, children }) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">{title}</Typography>
                {action}
            </Stack>
            {children}
        </CardContent>
    </Card>
);

type Visible = { showClassic: boolean; showDigital: boolean };

// In percent mode the controls replace a series' value and keep the number under `__full`.
const raw = (row: Record<string, unknown>, key: string) =>
    Number(row?.[`${key}__full`] ?? row?.[key] ?? 0);

/**
 * The chart controls' tooltip slot. Lists each type's expected and delivered, and their totals, so
 * stacked bars can be read without adding the segments up.
 */
export const splitTooltip = ({
    labels,
    colors,
    showClassic,
    showDigital,
    formatLabel,
}: {
    labels: SeriesLabels;
    colors: SeriesColors;
    formatLabel?: (l: string) => string;
} & Visible) =>
    function SplitTooltip({ active, payload, label }: TooltipProps<number, string>) {
        if (!active || !payload?.length) return null;
        const row = payload[0].payload as Record<string, unknown>;
        // Chart rows are keyed by the series labels, as the Summary's are, so the legend reads.
        const classic = raw(row, labels.classic);
        const digital = raw(row, labels.digital);
        const expectedClassic = raw(row, labels.expectedClassic);
        const expectedDigital = raw(row, labels.expectedDigital);
        const both = showClassic && showDigital;
        // The percent control swaps a delivered series for its % of its expected and keeps the
        // number under `__full`; say both, as the site's tooltip does.
        const percentMode =
            row[`${labels.classic}__full`] !== undefined ||
            row[`${labels.digital}__full`] !== undefined;
        type Line = { name: string; value: number; color: string; of?: number; opacity?: number };
        const lines = [
            !percentMode &&
                showClassic && {
                    name: labels.expectedClassic,
                    value: expectedClassic,
                    color: colors.classic,
                    opacity: EXPECTED_OPACITY,
                },
            !percentMode &&
                showDigital && {
                    name: labels.expectedDigital,
                    value: expectedDigital,
                    color: colors.digital,
                    opacity: EXPECTED_OPACITY,
                },
            !percentMode &&
                both && {
                    name: labels.expected,
                    value: expectedClassic + expectedDigital,
                    color: colors.expected,
                },
            showClassic && {
                name: labels.classic,
                value: classic,
                color: colors.classic,
                of: expectedClassic,
            },
            showDigital && {
                name: labels.digital,
                value: digital,
                color: colors.digital,
                of: expectedDigital,
            },
            both && {
                name: labels.total,
                value: classic + digital,
                color: colors.both,
                of: expectedClassic + expectedDigital,
            },
        ].filter(Boolean) as Line[];

        // Same shell and type as CustomTooltip, the tooltip on every other report chart.
        return (
            <Paper elevation={4} sx={{ p: 2 }}>
                <Box maxWidth="25em">
                    <Typography variant="subtitle2" noWrap>
                        {formatLabel ? formatLabel(String(label)) : label}
                    </Typography>
                </Box>
                <Box>
                    {lines.map(l => (
                        <Box key={l.name} sx={{ color: l.color, opacity: l.opacity ?? 1 }}>
                            <Typography variant="body1">
                                {l.name}: {whole(l.value)}
                                {percentMode && l.of > 0 && (
                                    <> ({formatNumber((l.value / l.of) * 100)}%)</>
                                )}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>
        );
    };

/**
 * The shared chart wraps its brush in `pl={9} width="95%"`, content-box, so the padding lands on
 * top of the width: fine full-width, but on a half-width card the brush runs out of the box.
 * Border-box keeps the same inset while fitting any width.
 */
const SLIDER_WRAPPER_PROPS = { boxSizing: 'border-box' } as const;

type WidgetRow = {
    expected: number;
    expectedClassic?: number;
    expectedDigital?: number;
    classic: number;
    digital: number;
    total: number;
};

/** Expected is each type's own colour, lighter, so it reads as that type's target. */
const EXPECTED_OPACITY = 0.35;

const DEFAULT_CONTROLS: ReportControlsState = {
    showPercent: false,
    showNumbers: false,
    viewFilter: 'Both',
    cumulative: true,
};

/**
 * One Combined chart, drawn by the Summary's own chart widget so it carries the same controls:
 * expected / delivered / both, numbers, percent, sort, table view, export and fullscreen, and
 * accumulate on the running total.
 *
 * Rows are keyed by the series labels, as the Summary's are, so legends, tables and exports read.
 * On bars, Expected and Total are each one bar stacked from their classic and digital parts. Each
 * expected series is named "<type> Expected" so the controls pair it with its own type: in percent
 * mode a type reads against its own target, and the bars stop stacking, since two percentages of
 * different targets don't add up. The Total column exists so sort and export have it.
 */
export const CombinedWidget: React.FC<
    {
        id: string;
        kind: 'bar' | 'line';
        rows: Array<WidgetRow & { x: string }>;
        xAxisLabel: string;
        labels: SeriesLabels;
        colors: SeriesColors;
        namePrefix: string;
        startDate?: Date;
        endDate?: Date;
        isLoading: boolean;
        height?: number;
    } & Visible
> = ({
    id,
    kind,
    rows,
    xAxisLabel,
    labels,
    colors,
    namePrefix,
    startDate,
    endDate,
    isLoading,
    height = 360,
    showClassic,
    showDigital,
}) => {
    // Held here rather than inside the controls, because the bars' stacking depends on it.
    const [controls, setControls] = useState<ReportControlsState>(DEFAULT_CONTROLS);
    const stack = kind === 'bar' && !controls.showPercent;
    const splitExpected = kind === 'bar';

    const data = useMemo(
        () =>
            rows.map(r => ({
                [xAxisLabel]: r.x,
                ...(splitExpected
                    ? {
                          ...(showClassic
                              ? { [labels.expectedClassic]: r.expectedClassic ?? 0 }
                              : {}),
                          ...(showDigital
                              ? { [labels.expectedDigital]: r.expectedDigital ?? 0 }
                              : {}),
                      }
                    : { [labels.expected]: r.expected }),
                ...(showClassic ? { [labels.classic]: r.classic } : {}),
                ...(showDigital ? { [labels.digital]: r.digital } : {}),
                [labels.total]: r.total,
                [`${labels.total}__pct`]: r.expected ? (r.total / r.expected) * 100 : 0,
            })),
        [rows, xAxisLabel, labels, showClassic, showDigital, splitExpected],
    );

    const parts = useMemo<ChartReportPart[]>(() => {
        const series = (
            key: string,
            color: string,
            stackId?: string,
            role: 'expected' | 'delivered' = 'delivered',
        ): ChartReportPart => ({
            dataKey: key,
            filterRole: role,
            // A translucent fill rather than fill-opacity: the legend swatch copies the fill but
            // ignores fill-opacity, so expected and delivered would look the same there.
            barProps: {
                fill: role === 'expected' ? alpha(color, EXPECTED_OPACITY) : color,
                ...(stackId ? { stackId } : {}),
            },
            lineProps: { stroke: color, strokeWidth: 2 },
        });
        const expectedParts: ChartReportPart[] = splitExpected
            ? [
                  ...(showClassic
                      ? [
                            series(
                                labels.expectedClassic,
                                colors.classic,
                                stack ? 'expected' : undefined,
                                'expected',
                            ),
                        ]
                      : []),
                  ...(showDigital
                      ? [
                            series(
                                labels.expectedDigital,
                                colors.digital,
                                stack ? 'expected' : undefined,
                                'expected',
                            ),
                        ]
                      : []),
              ]
            : [
                  {
                      dataKey: labels.expected,
                      filterRole: 'expected',
                      lineProps: {
                          stroke: colors.expected,
                          strokeDasharray: '5 4',
                          strokeWidth: 2,
                      },
                  },
              ];
        return [
            ...expectedParts,
            ...(showClassic
                ? [series(labels.classic, colors.classic, stack ? 'total' : undefined)]
                : []),
            ...(showDigital
                ? [series(labels.digital, colors.digital, stack ? 'total' : undefined)]
                : []),
            // A line can't stack, so the running total draws its own Total line.
            ...(kind === 'line' && showClassic && showDigital
                ? [series(labels.total, colors.both)]
                : []),
        ];
    }, [kind, labels, colors, showClassic, showDigital, stack, splitExpected]);

    const renderTooltipLabel = useMemo(
        () => splitTooltip({ labels, colors, showClassic, showDigital }),
        [labels, colors, showClassic, showDigital],
    );

    return (
        <StaticChartWidgetReport
            widgetId={id}
            reportType={kind === 'line' ? ReportType.Line : ReportType.Bar}
            namePrefix={namePrefix}
            startDate={startDate}
            endDate={endDate}
            xAxisDataKey={xAxisLabel}
            parts={parts}
            data={data}
            loadingState={isLoading ? NetworkRequestState.InProgress : NetworkRequestState.Success}
            renderTooltipLabel={renderTooltipLabel}
            allowPercentToggle
            allowAccumulateToggle={kind === 'line'}
            sortDataKey={kind === 'bar' ? labels.total : undefined}
            controls={controls}
            onControlsChange={setControls}
            filterOptionLabels={{
                expected: labels.expected,
                delivered: labels.total,
                both: `${labels.expected} & ${labels.total}`,
            }}
            height={height}
            sliderWrapperProps={SLIDER_WRAPPER_PROPS}
            // Bars go without a key on this report; the tooltip names every segment.
            hideLegend={kind === 'bar'}
        />
    );
};
